import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Icon } from '../../components/Icon.tsx';
import { useCreateIssue, useZoneMe } from '../../features/zone/api.ts';
import { zoneErrorMessage } from '../../features/zone/errors.ts';
import { resizeToJpeg } from '../../features/zone/photoResize.ts';
import { SelectField } from '../../components/SelectField.tsx';
import { PriorityPicker } from '../../features/zone/PriorityPicker.tsx';
import { type IssuePriority } from '../../features/zone/types.ts';

const MAX_PHOTOS = 5;

interface Photo { id: number; blob: Blob; url: string }
interface Coords { lat: number; lng: number; accuracy: number }

export function ZoneNewIssue(): JSX.Element {
  const navigate = useNavigate();
  const { data: me, isLoading } = useZoneMe();
  const create = useCreateIssue();

  const [zoneId, setZoneId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<IssuePriority>('NORMAL');
  const [locationText, setLocationText] = useState('');
  const [coords, setCoords] = useState<Coords | null>(null);
  const [locating, setLocating] = useState(false);
  const [locError, setLocError] = useState<string | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [processing, setProcessing] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const nextId = useRef(0);
  const photosRef = useRef<Photo[]>([]);
  photosRef.current = photos;

  // Revoke any remaining thumbnail object URLs on unmount.
  useEffect(() => () => { photosRef.current.forEach((p) => URL.revokeObjectURL(p.url)); }, []);

  const myZones = me?.myZones ?? [];
  const effectiveZone = myZones.length === 1 ? myZones[0]!.id : zoneId;

  if (isLoading) {
    return <div className="page-narrow"><p className="status-label" role="status">Loading…</p></div>;
  }
  if (myZones.length === 0) {
    return (
      <div className="page-narrow space-y-4">
        <h1 className="text-3xl leading-tight tracking-tight">New issue</h1>
        <p className="text-sm text-muted-foreground">You are not assigned to a zone yet. Ask your council admin.</p>
        <Link to="/zone" className="btn-ghost">Back to issues</Link>
      </div>
    );
  }

  const getLocation = (): void => {
    setLocError(null);
    if (!('geolocation' in navigator)) {
      setLocError('Location is not available on this device.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: Math.round(pos.coords.accuracy) });
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        setLocError(
          err.code === err.PERMISSION_DENIED
            ? 'Location permission was denied. You can still describe the place in words.'
            : 'Could not get your location. Try again or describe the place in words.',
        );
      },
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };

  const addPhotos = async (files: FileList | null): Promise<void> => {
    if (!files || files.length === 0) return;
    setPhotoError(null);
    const room = MAX_PHOTOS - photos.length;
    const picked = Array.from(files).slice(0, Math.max(0, room));
    if (files.length > picked.length) setPhotoError(`You can attach up to ${MAX_PHOTOS} photos.`);
    setProcessing(true);
    const added: Photo[] = [];
    for (const f of picked) {
      try {
        const blob = await resizeToJpeg(f);
        added.push({ id: nextId.current++, blob, url: URL.createObjectURL(blob) });
      } catch {
        setPhotoError('One of the photos could not be read. Try another image.');
      }
    }
    setPhotos((cur) => [...cur, ...added].slice(0, MAX_PHOTOS));
    setProcessing(false);
  };

  const removePhoto = (id: number): void => {
    setPhotos((cur) => {
      const gone = cur.find((p) => p.id === id);
      if (gone) URL.revokeObjectURL(gone.url);
      return cur.filter((p) => p.id !== id);
    });
    setPhotoError(null);
  };

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    setError(null);
    if (!effectiveZone) { setError('Choose a zone.'); return; }
    const form = new FormData();
    form.append('zoneId', effectiveZone);
    form.append('title', title.trim());
    form.append('description', description.trim());
    form.append('priority', priority);
    if (locationText.trim()) form.append('locationText', locationText.trim());
    if (coords) {
      form.append('lat', String(coords.lat));
      form.append('lng', String(coords.lng));
    }
    photos.forEach((p, i) => form.append('photos', p.blob, `photo-${i + 1}.jpg`));
    create.mutate(form, {
      onSuccess: (issue) => { void navigate({ to: '/zone/issues/$id', params: { id: issue.id } }); },
      onError: (err) => setError(zoneErrorMessage(err)),
    });
  };

  const canSubmit = !!effectiveZone && title.trim().length > 0 && description.trim().length > 0 && !create.isPending && !processing;

  return (
    <div className="page-narrow">
      <form onSubmit={submit} className="space-y-6">
        <header className="flex items-center gap-2">
          <Link to="/zone" aria-label="Back to issues" className="btn-ghost btn-icon -ms-2">
            <Icon name="arrow_back" size={22} />
          </Link>
          <h1 className="text-3xl leading-tight tracking-tight">New issue</h1>
        </header>

        {myZones.length > 1 && (
          <div className="space-y-1.5">
            <label className="field-label" htmlFor="zone">Zone</label>
            <SelectField
              id="zone" label="Zone" placeholder="Choose a zone"
              value={zoneId} onChange={setZoneId}
              options={myZones.map((z) => ({ value: z.id, label: z.name }))}
            />
          </div>
        )}
        {myZones.length === 1 && (
          <p className="text-sm text-muted-foreground">Zone: <span className="font-medium text-ink">{myZones[0]!.name}</span></p>
        )}

        <div className="space-y-1.5">
          <label className="field-label" htmlFor="title">Title</label>
          <input id="title" required maxLength={140} className="input text-base" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>

        <div className="space-y-1.5">
          <label className="field-label" htmlFor="description">Description</label>
          <textarea id="description" required maxLength={4000} rows={5} className="textarea text-base" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <PriorityPicker value={priority} onChange={setPriority} />

        <div className="space-y-2">
          <label className="field-label" htmlFor="location">Location</label>
          <input
            id="location"
            maxLength={200}
            className="input text-base"
            placeholder="Street, landmark or area"
            value={locationText}
            onChange={(e) => setLocationText(e.target.value)}
          />
          {coords ? (
            <p className="flex items-center gap-1.5 text-sm text-[hsl(var(--success))]" role="status">
              <Icon name="check_circle" size={18} />
              Location added ✓ (±{coords.accuracy}m)
              <button type="button" aria-label="Remove location" className="btn-ghost btn-icon btn-sm" onClick={() => setCoords(null)}>
                <Icon name="close" size={18} />
              </button>
            </p>
          ) : (
            <button type="button" className="btn-ghost -ms-4" onClick={getLocation} disabled={locating}>
              <Icon name="my_location" size={18} /> {locating ? 'Getting location…' : 'Use my location'}
            </button>
          )}
          {locError && <p className="text-sm text-destructive" role="alert">{locError}</p>}
        </div>

        <div className="space-y-2">
          <span className="field-label">Photos <span className="font-normal text-muted-foreground">({photos.length}/{MAX_PHOTOS})</span></span>
          {photos.length > 0 && (
            <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {photos.map((p, i) => (
                <li key={p.id} className="relative aspect-square overflow-hidden rounded-2xl border border-[hsl(var(--border)/0.7)]">
                  <img src={p.url} alt={`Photo ${i + 1}`} className="size-full object-cover" />
                  <button
                    type="button"
                    aria-label={`Remove photo ${i + 1}`}
                    onClick={() => removePhoto(p.id)}
                    className="absolute end-1 top-1 grid size-8 place-items-center rounded-full bg-black/60 text-white"
                  >
                    <Icon name="close" size={18} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {photos.length < MAX_PHOTOS && (
            <div className="flex flex-wrap gap-2">
              {/* Two pickers: `capture` forces the camera on phones, so the gallery needs its own input. */}
              {([
                { key: 'camera', icon: 'photo_camera', label: 'Take photo', capture: true },
                { key: 'gallery', icon: 'image', label: 'Upload photos', capture: false },
              ] as const).map((o) => (
                <label key={o.key} className={`btn-secondary flex-1 cursor-pointer whitespace-nowrap px-4 sm:flex-none sm:px-6 ${processing ? 'pointer-events-none opacity-50' : ''}`}>
                  <Icon name={o.icon} size={18} /> {processing ? 'Processing…' : o.label}
                  <input
                    type="file"
                    accept="image/*"
                    {...(o.capture ? { capture: 'environment' as const } : { multiple: true })}
                    className="sr-only"
                    onChange={(e) => {
                      const input = e.currentTarget;
                      void addPhotos(input.files).finally(() => { input.value = ''; });
                    }}
                  />
                </label>
              ))}
            </div>
          )}
          {photoError && <p className="text-sm text-destructive" role="alert">{photoError}</p>}
        </div>

        {error && <p className="text-sm text-destructive" role="alert">{error}</p>}

        <div className="sticky bottom-[calc(5.5rem+env(safe-area-inset-bottom))] -mx-4 bg-[hsl(var(--background))] px-4 py-3 md:static md:mx-0 md:px-0 md:py-0">
          <button type="submit" disabled={!canSubmit} className="btn-primary w-full md:w-auto">
            {create.isPending ? 'Submitting…' : 'Submit issue'}
          </button>
        </div>
      </form>
    </div>
  );
}
